import { EmptyToday } from '../components/EmptyToday.tsx';
import { PlanningSummary } from '../components/PlanningSummary.tsx';
import { hasCalendarContent } from '../ui/entryExperience.ts';
import { useMemo } from 'react';
import { ArrowUpRight, BellRing, CalendarDays, FileUp, ListChecks, Plus, Sun, TriangleAlert } from 'lucide-react';
import { addDays, collectItems, deadlineOverview, isoWeek, startOfIsoWeek, itemsOnDay, Temporal, todayIn } from '@autoday/core';
import { useLang } from '../i18n/i18n.ts';
import { formatDateLong, formatInstant, monthShort, weekdayName } from '../i18n/format.ts';
import { sourceFreshness } from '../ui/sources.ts';
import { getStore } from '../store/appStore.ts';
import { useStore } from '../store/appStore.ts';
import { navigate, useToday } from '../ui/hooks.ts';
import { Button, Panel } from '../ui/kit.tsx';
import { AgendaRow } from '../components/Agenda.tsx';
import { useUi } from '../App.tsx';
import { useNotifications } from '../notifications/service.ts';
import { StateIcon, useReminderGuardian } from './NotificationStatusView.tsx';
import { prepWhen } from './ImportantView.tsx';
import { usePrepAttention } from '../prep/drafts.ts';
import { attentionSummary } from '../components/prep/prepText.ts';
import { NextStep } from '../components/NextStep.tsx';

export function TodayView() {
  const { state } = useStore();
  const notif = useNotifications();
  const ui = useUi();
  const { t } = useLang();
  const today = useToday();
  const tz = state.settings.timeZone;
  const items = itemsOnDay(collectItems(state, addDays(today, -2), addDays(today, 3)), today, tz);
  // Samma underlag som Viktigt: inga egna räkningar här, så att siffrorna alltid stämmer överens.
  const overview = deadlineOverview(state, today, 60);
  // Förberedelseplaner: samma delade beräkning som Viktigt, så siffrorna stämmer mellan vyerna.
  const attention = usePrepAttention();
  const prepSummary = attentionSummary(attention.drafts.length, attention.reviews.length);
  const important = overview.entries.filter((e) => e.group !== 'done').slice(0, 6);
  const missed = notif.plan?.missed ?? [];
  const guardian = useReminderGuardian().result;
  const imported = sourceFreshness(state, getStore().clock.now().epochMilliseconds);

  const weekStart = startOfIsoWeek(today);
  const week = useMemo(() => {
    const all = collectItems(state, addDays(weekStart, -2), addDays(weekStart, 9));
    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(weekStart, index);
      return { date, count: itemsOnDay(all, date, tz).length };
    });
  }, [state, weekStart, tz]);
  const remaining = items.filter((item) => !item.done).length;
  const date = Temporal.PlainDate.from(today);
  if (!hasCalendarContent(state)) return <EmptyToday />;

  return (
    <div className="autoday-today mx-auto flex max-w-6xl flex-col gap-5 px-4 py-5 lg:px-8 lg:py-8">
      <header className="autoday-day-header flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="autoday-date-tile" aria-hidden="true">
            <span>{monthShort(date.month)}</span>
            <strong>{date.day}</strong>
          </div>
          <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-widest text-accent">{t('Din dag, i lugn och ro', 'Your day, calmly')}</p>
          <h1 className="text-3xl font-semibold tracking-tight">{t('Idag', 'Today')}</h1>
          <p className="text-sm text-muted">
            {formatDateLong(today)} · {t('vecka', 'week')} {isoWeek(today).week}
          </p>
          </div>
        </div>
        <Button variant="primary" icon={<Plus size={18} aria-hidden />} onClick={() => ui.openEditor({ mode: 'new', date: today })}>
          {t('Ny aktivitet', 'New activity')}
        </Button>
      </header>

      <section aria-label={t('Veckans dagar', 'Days of the week')} className="autoday-week-panel">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm">
          <h2 className="font-semibold">{t('Din vecka', 'Your week')} <span className="ml-1 font-normal text-muted">{isoWeek(today).week}</span></h2>
          <a href="#/kalender" className="inline-flex min-h-11 items-center gap-1 text-accent">{t('Öppna kalender', 'Open calendar')} <ArrowUpRight size={16} aria-hidden /></a>
        </div>
        <div className="autoday-week-grid">
          {week.map((day) => (
            <button key={day.date} className="autoday-week-day" aria-current={day.date === today ? 'date' : undefined}
              aria-label={`${formatDateLong(day.date)}, ${day.count} ${day.count === 1 ? t('aktivitet', 'activity') : t('aktiviteter', 'activities')}`}
              onClick={() => { ui.setFocusDate(day.date); navigate('kalender'); }}>
              <span className="text-xs capitalize">{weekdayName(Temporal.PlainDate.from(day.date).dayOfWeek, true)}</span>
              <strong className="text-xl">{Temporal.PlainDate.from(day.date).day}</strong>
              <span className="autoday-week-marker" aria-hidden="true">{day.count ? <>{day.count}<span className="autoday-week-count-label">{t(' st', '')}</span></> : '–'}</span>
            </button>
          ))}
        </div>
      </section>

      <a href="#/utrymme" className="flex min-h-16 flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-accent-soft p-4 text-text"><span><strong className="block text-sm">{t('Får det plats?', 'Will it fit?')}</strong><span className="text-sm text-muted">{t('Pröva ett nytt åtagande innan du säger ja.', 'Try a new commitment before you say yes.')}</span></span><ArrowUpRight size={20} aria-hidden /></a>
      <NextStep />
      <PlanningSummary />
      <a href="#/planera" className="flex min-h-16 flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-panel p-4"><span><strong className="block text-sm">{t('Planera med marginal', 'Plan with room to spare')}</strong><span className="text-sm text-muted">{t('Grundplanering gratis · avancerade förslag med Plus.', 'Basic planning free · advanced proposals with Plus.')}</span></span><ArrowUpRight size={20} aria-hidden /></a>

      {missed.length > 0 && (
        <Panel className="border-warn/50 p-4" aria-live="polite">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-warn">
            <TriangleAlert size={16} aria-hidden />{' '}
            {t(`${missed.length} påminnelse${missed.length > 1 ? 'r' : ''} passerade medan AutoDay var stängd`, `${missed.length} reminder${missed.length > 1 ? 's' : ''} passed while AutoDay was closed`)}
          </h2>
          <ul className="mt-2 space-y-1 text-sm">
            {missed.slice(0, 5).map((m) => (
              <li key={m.pointId}>
                <button className="text-left underline-offset-2 hover:underline" onClick={() => ui.openItem({ ref: m.ref.kind === 'local' ? m.ref.id : m.ref.key, occurrenceKey: m.occurrenceKey })}>
                  {formatInstant(m.fireAt, tz)} – {m.title}
                </button>
              </li>
            ))}
          </ul>
          {missed.length > 5 && <p className="mt-1 text-xs text-muted">{t(`… och ${missed.length - 5} till. Alla finns under Viktigt och i kalendern.`, `… and ${missed.length - 5} more. All are under Deadlines and in the calendar.`)}</p>}
        </Panel>
      )}

      {/* grid-cols-1 = minmax(0,1fr): en lång titel får inte bredda den enda kolumnen på mobil. */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <Panel className="p-2">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 pb-3 pt-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold"><CalendarDays size={17} className="text-accent" aria-hidden />{t('Dagens plan', 'Today’s plan')}</h2>
            <span className="text-xs text-muted">{items.length ? t(`${remaining} kvar av ${items.length}`, `${remaining} of ${items.length} left`) : t('Plats för det som är viktigt', 'Room for what matters')}</span>
          </div>
          {items.length ? (
            <ul>{items.map((i) => <AgendaRow key={i.uid} item={i} today={today} tz={tz} />)}</ul>
          ) : (
            <div className="autoday-empty-day">
              <span className="autoday-empty-icon"><Sun size={28} aria-hidden /></span>
              <p className="text-lg font-semibold text-text">{t('Lite luft i kalendern.', 'Some breathing room.')}</p>
              <p className="max-w-xs text-sm text-muted">{t('Inget inplanerat idag. Samla möten, vardagsplaner och små saker att komma ihåg här.', 'Nothing planned today. Gather meetings, everyday plans and small things to remember here.')}</p>
              <Button className="mt-2" icon={<Plus size={16} aria-hidden />} onClick={() => ui.openEditor({ mode: 'new', date: today })}>{t('Lägg till en aktivitet', 'Add an activity')}</Button>
            </div>
          )}
        </Panel>

        <div className="flex flex-col gap-5">
          <Panel className="p-2">
            <div className="flex items-center justify-between px-3 pb-1 pt-2">
              <h2 className="text-sm font-semibold text-muted">{t('Närmar sig', 'Coming up')}</h2>
              <a href="#/viktigt" className="text-xs font-medium text-accent hover:underline">
                {t('Visa alla', 'Show all')}
              </a>
            </div>
            {overview.attention > 0 && (
              <p className="px-3 text-xs text-muted">
                {[
                  overview.counts.review ? t(`${overview.counts.review} att granska`, `${overview.counts.review} to review`) : null,
                  overview.counts.passed ? t(`${overview.counts.passed} passerade utan klarmarkering`, `${overview.counts.passed} passed, not marked done`) : null,
                  overview.counts.soon ? t(`${overview.counts.soon} inom 7 dagar`, `${overview.counts.soon} within 7 days`) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
            {important.length ? (
              <ul>
                {important.map((e) => (
                  <AgendaRow
                    key={e.item.uid}
                    item={e.item}
                    today={today}
                    tz={tz}
                    showDate
                    extra={e.prep.some((p) => !p.done) ? <span className="text-accent">{t('Förberedelse', 'Preparation')}: {prepWhen(e.prep.find((p) => !p.done)!)}</span> : undefined}
                  />
                ))}
              </ul>
            ) : (
              <p className="px-3 py-4 text-sm text-muted">{t('Inga viktiga datum de närmaste 60 dagarna.', 'No important dates in the next 60 days.')}</p>
            )}
            {overview.counts.review > 0 && (
              <p className="mx-3 mb-2 mt-1 rounded-lg border border-warn/50 bg-warn-bg px-3 py-2 text-xs">
                {t(`${overview.counts.review} deadline${overview.counts.review > 1 ? 's' : ''} väntar på granskning efter en ny import.`, `${overview.counts.review} deadline${overview.counts.review > 1 ? 's' : ''} awaiting review after a new import.`)}{' '}
                <a className="font-semibold underline" href="#/viktigt">
                  {t('Granska', 'Review')}
                </a>
              </p>
            )}
            {prepSummary && (
              <p className="mx-3 mb-2 mt-1 flex items-start gap-2 rounded-lg border border-accent-line bg-accent-soft/40 px-3 py-2 text-xs" role="status">
                <ListChecks size={14} aria-hidden className="mt-0.5 shrink-0 text-accent" />
                <span>
                  {prepSummary}.{' '}
                  <a className="font-semibold underline" href="#/viktigt">
                    {t('Visa i Viktigt', 'Show in Deadlines')}
                  </a>
                </span>
              </p>
            )}
          </Panel>

          <Panel className="p-4" aria-labelledby="today-imports">
            <h2 id="today-imports" className="flex items-center gap-2 text-sm font-semibold">
              <FileUp size={16} className="text-muted" aria-hidden /> {t('Importerade kalendrar', 'Imported calendars')}
            </h2>
            {imported.length ? (
              <ul className="mt-1 space-y-1 text-sm">
                {imported.map((s) => (
                  <li key={s.id}>
                    <span className="font-medium">{s.name}</span>{' '}
                    <span className="text-muted">
                      – {t('senast uppdaterad', 'last updated')} {formatDateLong(todayIn(tz, Temporal.Instant.from(s.lastImportAt)).toString())}
                    </span>
                    {s.stale && (
                      <span className="block text-xs text-warn">
                        {t(`Exporten är ${s.ageDays} dagar gammal. Ändringar i ${s.name} syns inte här förrän du `, `The export is ${s.ageDays} days old. Changes in ${s.name} do not show here until you `)}
                        <a className="underline" href="#/import">
                          {t('importerar en ny export', 'import a new export')}
                        </a>
                        .
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-muted">
                {t('Ingen kalender importerad.', 'No calendar imported.')}{' '}
                <a className="font-medium text-accent hover:underline" href="#/import">
                  {t('Importera en .ics-fil', 'Import an .ics file')}
                </a>
                {t(' från din kalender.', ' from your calendar.')}
              </p>
            )}
          </Panel>

          <Panel className="p-4" aria-labelledby="today-guardian">
            <h2 id="today-guardian" className="flex items-center gap-2 text-sm font-semibold">
              <BellRing size={16} className="text-muted" aria-hidden /> {t('Påminnelser', 'Reminders')}
            </h2>
            <p className="mt-1 flex items-start gap-2 text-sm">
              <StateIcon state={guardian.overall === 'ok' ? 'ok' : guardian.overall === 'risk' ? 'risk' : guardian.overall === 'unknown' ? 'unknown' : 'attention'} />
              <span>{guardian.headline}</span>
            </p>
            {notif.report?.coverageUntil && (notif.report.channel === 'android-local' || notif.report.channel === 'ios-local') && (
              <p className="mt-1 text-xs text-muted">
                {t('Lokala notiser schemalagda till', 'Local notifications scheduled until')} {formatInstant(notif.report.coverageUntil, tz)}.
              </p>
            )}
            <a href="#/notiser" className="mt-2 inline-flex min-h-11 items-center text-sm font-medium text-accent hover:underline">
              {guardian.overall === 'ok' ? t('Öppna Påminnelsekoll', 'Open Reminder check') : t('Se vad som behöver göras', 'See what needs to be done')}
            </a>
          </Panel>
        </div>
      </div>
      <p className="text-xs text-muted">{t('AutoDay hjälper dig hålla koll men kan inte garantera att ingen deadline missas. Kontrollera viktiga datum mot originalkällan.', 'AutoDay helps you keep track but cannot guarantee that no deadline is missed. Check important dates against the original source.')}</p>
    </div>
  );
}
